import { useState } from "react";
import AlignmentToolSelect from "@/components/common/AlignmentToolSelect";
import MetadataBuilder from "@/components/common/MetadataBuilder";
const MIN_PHRED_QUALITY_SCORE = 10;
const MAX_PHRED_QUALITY_SCORE = 40;
const MIN_READ_LENGTH = 50;
const MAX_READ_LENGTH = 120;

export const defaultValues = {
  organism: "",
  alignment_tool: "",
  target_bed_file_link: "",
  raw_data_qc: {
    phred_quality_score: 30,
    minimum_read_length_required: 75,
  },
  metadata_builder: [
    {
      sample_id: "",
    },
  ],
};

export const validate = (values = {}) => {
  const errors = {};
  const rawDataQc = values.raw_data_qc || {};

  if (!String(values.organism || "").trim()) {
    errors.organism = "Organism is required.";
  }
  if (!String(values.alignment_tool || "").trim()) {
    errors.alignment_tool = "Alignment tool is required.";
  }

  if (!String(rawDataQc.phred_quality_score || "").trim()) {
    errors.phred_quality_score = "Phred quality score is required.";
  } else {
    const numericValue = Number(rawDataQc.phred_quality_score);
    if (Number.isNaN(numericValue) || numericValue < MIN_PHRED_QUALITY_SCORE || numericValue > MAX_PHRED_QUALITY_SCORE) {
      errors.phred_quality_score = `Value must be between ${MIN_PHRED_QUALITY_SCORE} and ${MAX_PHRED_QUALITY_SCORE}.`;
    }
  }

  if (!String(rawDataQc.minimum_read_length_required || "").trim()) {
    errors.minimum_read_length_required = "Minimum read length is required.";
  } else {
    const numericValue = Number(rawDataQc.minimum_read_length_required);
    if (Number.isNaN(numericValue) || numericValue < MIN_READ_LENGTH || numericValue > MAX_READ_LENGTH) {
      errors.minimum_read_length_required = `Value must be between ${MIN_READ_LENGTH} and ${MAX_READ_LENGTH}.`;
    }
  }

  return errors;
};

export default function ExomeTargetedSeqParameters({
  values = {},
  onChange,
  errors = {},
  expectedSamples,
}) {
  const setValue = (key, value) => onChange(key, value);
  const updateRawDataQc = (key, value) => {
    setValue("raw_data_qc", { ...(values.raw_data_qc || {}), [key]: value });
  };

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-[#E5E7EB] bg-[#F8FAF7] p-4">
        <h3 className="text-sm font-semibold text-[#09582D]">Basic Analysis Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Organism
            </label>

            <select
              value={values.organism || ""}
              onChange={(e) => setValue("organism", e.target.value)}
              className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm focus:border-[#538135] focus:outline-none focus:ring-2 focus:ring-[#EAF4E3]"
            >
              <option value="">Select Organism</option>
              <option value="Homo sapiens (hg19)">Homo sapiens (hg19)</option>
              <option value="Homo sapiens (hg38)">Homo sapiens (hg38)</option>
            </select>

            {errors.organism && (
              <p className="mt-1 text-xs text-red-600">
                {errors.organism}
              </p>
            )}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">
              Alignment Tool
            </label>

            <AlignmentToolSelect
              value={values.alignment_tool || ""}
              onChange={(value) => setValue("alignment_tool", value)}
              options={["BWA", "Bowtie2"]}
            />

            {errors.alignment_tool && (
              <p className="mt-1 text-xs text-red-600">
                {errors.alignment_tool}
              </p>
            )}
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="rounded-lg border border-[#E5E7EB] p-4">
          <MetadataBuilder
            value={values.metadata_builder || []}
            onChange={(metadata) => setValue("metadata_builder", metadata)}
            errors={errors}
            expectedSamples={expectedSamples}
          />
        </div>
      </div>

      <div className="mt-6">
        <div className="rounded-lg border border-[#E5E7EB] p-4">
          <div className="md:col-span-2">
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Target BED File Link</label>
            <input
              type="url"
              value={values.target_bed_file_link || ""}
              onChange={(e) => setValue("target_bed_file_link", e.target.value)}
              placeholder="https://..."
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Optional. Provide a BED file link if target regions are to be used for analysis.</p>
          </div>
        </div>
      </div>


      <div className="rounded-lg border border-[#E5E7EB] p-4">
        <h3 className="text-sm font-semibold text-gray-800">Raw Data QC Parameters</h3>
        <div className="mt-4 grid gap-4 md:grid-cols-2">
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Phred Quality Score</label>
            <input
              type="number"
              min={MIN_PHRED_QUALITY_SCORE}
              max={MAX_PHRED_QUALITY_SCORE}
              value={values.raw_data_qc?.phred_quality_score ?? ""}
              onChange={(e) => updateRawDataQc("phred_quality_score", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_PHRED_QUALITY_SCORE} to {MAX_PHRED_QUALITY_SCORE}</p>
            {errors.phred_quality_score && <p className="mt-1 text-xs text-red-600">{errors.phred_quality_score}</p>}
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Minimum Read Length Required</label>
            <input
              type="number"
              min={MIN_READ_LENGTH}
              max={MAX_READ_LENGTH}
              value={values.raw_data_qc?.minimum_read_length_required ?? ""}
              onChange={(e) => updateRawDataQc("minimum_read_length_required", e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#EAF4E3] focus:border-[#538135]"
            />
            <p className="mt-1 text-xs text-gray-500">Allowed range: {MIN_READ_LENGTH} to {MAX_READ_LENGTH}</p>
            {errors.minimum_read_length_required && <p className="mt-1 text-xs text-red-600">{errors.minimum_read_length_required}</p>}
          </div>
        </div>
      </div>
    </div>
  );
}
